import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
// SVG icons are now referenced directly from public folder for production compatibility
import frame1Icon from '../../assets/images/Frame1.svg'
import home1Icon from '../../assets/images/home1.svg'
import carOrdersIcon from '../../assets/images/carOrders.svg'
import talbetIcon from '../../assets/images/talbet.svg'


interface OrderTypeToggleProps {
  orderType: 'delivery' | 'takeaway' | 'dine_in' | 'car_pickup'
  onOrderTypeChange: (orderType: 'delivery' | 'takeaway' | 'dine_in' | 'car_pickup') => void
  allowedOrderTypes?: number[] // Array of allowed order type IDs from API (1=delivery, 2=takeaway, 3=dine_in, 4=car_pickup)
}

// Map API order type IDs to order type strings
const ORDER_TYPE_ID_MAP: Record<number, 'delivery' | 'takeaway' | 'dine_in' | 'car_pickup'> = {
  1: 'delivery',
  2: 'takeaway',
  3: 'dine_in',
  4: 'car_pickup'
}

const OrderTypeToggle: React.FC<OrderTypeToggleProps> = ({
  orderType,
  onOrderTypeChange,
  allowedOrderTypes = [] // Default to empty array (show all if not provided)
}) => {
  const { t } = useTranslation()
  
  // Order type configuration with translations
  const ORDER_TYPES_CONFIG = [
    {
      id: 'delivery',
      apiId: 1,
      label: t('order_type.delivery'),
      icon: talbetIcon
    },
    {
      id: 'takeaway',
      apiId: 2,
      label: t('order_type.takeaway'),
      icon: frame1Icon
    },
    {
      id: 'dine_in',
      apiId: 3,
      label: t('order_type.dine_in'),
      icon: home1Icon
    },
    {
      id: 'car_pickup',
      apiId: 4,
      label: t('order_type.car_pickup'),
      icon: carOrdersIcon
    }
  ]
  
  // Filter order types based on allowed order type IDs
  const availableOrderTypes = useMemo(() => {
    if (!allowedOrderTypes || allowedOrderTypes.length === 0) {
      // If no restrictions, show all types (backward compatibility)
      return ORDER_TYPES_CONFIG
    }
    
    // Map allowed IDs to order type strings and filter
    const allowedTypeStrings = allowedOrderTypes
      .map(id => ORDER_TYPE_ID_MAP[id])
      .filter(Boolean) as ('delivery' | 'takeaway' | 'dine_in' | 'car_pickup')[]
    
    return ORDER_TYPES_CONFIG.filter(type => 
      allowedTypeStrings.includes(type.id as any)
    )
  }, [allowedOrderTypes, t])

  // If no available types, return null
  if (availableOrderTypes.length === 0) {
    return null
  }

  return (
    <div className="h-14 bg-white rounded-xl px-4 flex items-center gap-3 shrink-0">
      {availableOrderTypes.map((typeConfig) => {
        const isSelected = orderType === typeConfig.id
        
        return (
          <button
            key={typeConfig.id}
            onClick={() => onOrderTypeChange(typeConfig.id as any)}
            className={`flex-1 h-10 rounded-full flex items-center justify-center gap-1 ${
              isSelected
                ? 'bg-[var(--brand-primary)] text-white shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.25)]' 
                : 'bg-white border border-[#EAEAE1] text-[#525866]'
            }`}
          >
            {/* Check if icon is a URL (from API) - use img tag, otherwise use CSS mask for local SVG */}
            {(() => {
              const iconSrc = typeof typeConfig.icon === 'string' 
                ? typeConfig.icon 
                : String(typeConfig.icon)
              
              // Use svg tag with image element inside to load external SVG files
              // Apply filter to change color when selected
              return (
                <svg 
                  className="w-5 h-5 flex-shrink-0"
                  viewBox="0 0 20 20"
                  aria-label={typeConfig.label}
                  style={{
                    filter: isSelected 
                      ? 'brightness(0) invert(1)' // White when selected
                      : 'brightness(0) saturate(0%)' // Gray when not selected
                  }}
                >
                  <foreignObject width="20" height="20">
                    <img 
                      src={iconSrc}
                      alt={typeConfig.label}
                      className="w-full h-full object-contain"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement
                        target.style.display = 'none'
                      }}
                    />
                  </foreignObject>
                </svg>
              )
            })()}
            <span className="text-sm font-medium">{typeConfig.label}</span>
          </button>
        )
      })}
    </div>
  )
}

export default OrderTypeToggle

